import { useState, useEffect } from 'react';
import { contentAPI, handleAPIError } from '../services/api';

export const useContent = (section) => {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchContent = async () => {
      if (!section) return;
      
      try {
        setLoading(true);
        setError(null);
        const data = await contentAPI.getContentSection(section);
        setContent(data.content);
      } catch (err) {
        setError(handleAPIError(err, `Failed to load ${section} content`));
        console.error('Error fetching content:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchContent();
  }, [section]);

  return {
    content,
    loading,
    error,
  };
};

export const useAllContent = () => {
  const [contentData, setContentData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchAllContent = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await contentAPI.getAllContent();
        
        // Convert array to object for easier access
        const contentMap = {};
        data.forEach(item => {
          contentMap[item.section] = item.content;
        });
        
        setContentData(contentMap);
      } catch (err) {
        setError(handleAPIError(err, 'Failed to load site content'));
        console.error('Error fetching all content:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchAllContent();
  }, []);

  return {
    contentData,
    loading,
    error,
  };
};

// Helper hook to inject custom CSS for color changes
export const useCustomStyles = (colors) => {
  useEffect(() => {
    if (!colors) return;

    // Create or update custom style element
    let styleElement = document.getElementById('custom-theme-styles');
    if (!styleElement) {
      styleElement = document.createElement('style');
      styleElement.id = 'custom-theme-styles';
      document.head.appendChild(styleElement);
    }

    // Generate CSS from colors
    const css = `
      :root {
        --custom-primary: ${colors.primary || '#000000'};
        --custom-accent: ${colors.accent || '#ea6c0a'};
        --custom-background: ${colors.background || '#ffffff'};
        --custom-text: ${colors.text || '#374151'};
      }
      
      /* Apply custom colours */
      .bg-black { background-color: var(--custom-primary) !important; }
      .text-black { color: var(--custom-primary) !important; }
      .bg-orange-600 { background-color: var(--custom-accent) !important; }
      .hover\\:bg-orange-700:hover { background-color: var(--custom-accent) !important; filter: brightness(0.9); }
      .text-orange-500 { color: var(--custom-accent) !important; }
      .text-orange-600 { color: var(--custom-accent) !important; }
      .border-orange-600 { border-color: var(--custom-accent) !important; }
      .hover\\:bg-orange-600:hover { background-color: var(--custom-accent) !important; }
      .focus\\:ring-orange-500:focus { --tw-ring-color: var(--custom-accent) !important; }
      
      /* Background colors */
      body { background-color: var(--custom-background); }
      .bg-white { background-color: var(--custom-background) !important; }
      
      /* Text colors */
      .text-gray-700 { color: var(--custom-text) !important; }
      .text-gray-600 { color: color-mix(in srgb, var(--custom-text) 80%, transparent) !important; }
    `;

    styleElement.textContent = css;

    // Cleanup function
    return () => {
      if (styleElement && styleElement.parentNode) {
        styleElement.parentNode.removeChild(styleElement);
      }
    };
  }, [colors]);
};